import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, Check, X, MessageSquare } from 'lucide-react';
import { market, PARTNER_LABELS, openPrivateFile } from '../lib/api';
import { DOC_LABELS } from './PartnerDocUpload';

const FILTERS = [['pending', 'Pending'], ['more_info', 'Info requested'], ['approved', 'Approved'], ['rejected', 'Rejected']];

function DecisionBar({ onDecide, busy }) {
  const [note, setNote] = useState('');
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <textarea data-testid="decision-note" value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note for the applicant (needed to reject or ask for info)" className="w-full rounded-lg border border-border px-3 py-2 text-sm"/>
      <div className="flex flex-wrap gap-2">
        <button data-testid="decision-approve" disabled={busy} onClick={() => onDecide('approve', note)} className="btn-primary !bg-[#1B5E20] !border-[#1B5E20] text-sm"><Check className="w-4 h-4"/>Approve</button>
        <button data-testid="decision-more-info" disabled={busy} onClick={() => onDecide('more_info', note)} className="btn-outline text-sm"><MessageSquare className="w-4 h-4"/>Ask for info</button>
        <button data-testid="decision-reject" disabled={busy} onClick={() => onDecide('reject', note)} className="btn-outline !text-[#B71C1C] !border-[#B71C1C] text-sm"><X className="w-4 h-4"/>Reject</button>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="text-sm"><span className="text-ink/60">{k}: </span><span className="font-medium">{v || '—'}</span></div>;
}

function AppDetail({ id, onDone }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { market.adminApplication(id).then(setA).catch(() => toast.error('Could not load')); }, [id]);
  if (!a) return <Loader2 className="w-5 h-5 animate-spin m-4"/>;
  const decide = async (decision, note) => {
    setBusy(true);
    try { await market.adminDecide(id, decision, note); toast.success('Saved'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="partner-app-detail" className="space-y-3 p-4 bg-[#FFFDF8] rounded-xl border border-border">
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
        <Row k="Type" v={PARTNER_LABELS[a.partner_type]}/><Row k="Constitution" v={a.constitution}/>
        <Row k="Owner" v={a.owner_name}/><Row k="Contact" v={`${a.contact_person} · ${a.mobile} · ${a.email}`}/>
        <Row k="Address" v={`${a.registered_address}, ${a.city}, ${a.state} ${a.pincode}`}/><Row k="Facility" v={a.facility_address}/>
        <Row k="PAN" v={a.pan_masked}/><Row k="GSTIN" v={a.gstin}/><Row k="Udyam" v={a.udyam}/>
        <Row k="Bank" v={`${a.bank_masked} · ${a.bank_ifsc} · ${a.bank_holder_name}`}/>
        <Row k="Brands" v={(a.brand_names || []).join(', ')}/><Row k="Trademarks" v={a.trademark_numbers}/>
        <Row k="Categories" v={(a.categories || []).join(', ')}/><Row k="Licences" v={a.licences}/>
      </div>
      {a.description && <p className="text-sm text-ink/80">{a.description}</p>}
      <div className="flex flex-wrap gap-2">
        {(a.documents || []).map(d => (
          <button key={d.url} data-testid={`partner-doc-open-${d.kind}`} onClick={() => openPrivateFile(d.url).catch(() => toast.error('Could not open file'))}
            className="pill !bg-white hover:!border-[#E67E22]"><FileText className="w-3.5 h-3.5"/>{DOC_LABELS[d.kind] || d.kind}</button>
        ))}
      </div>
      {(a.history || []).length > 0 && <div className="text-xs text-ink/60">{a.history.map(h => `${h.action} · ${new Date(h.at).toLocaleString()}${h.note ? ` · ${h.note}` : ''}`).join('  |  ')}</div>}
      {a.status !== 'approved' && <DecisionBar onDecide={decide} busy={busy}/>}
    </div>
  );
}

export default function AdminPartnerApprovals() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => market.adminApplications({ status }).then(setData).catch(() => setData({ items: [], counts: {} })), [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-partner-approvals" className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`partner-filter-${v}`} onClick={() => { setStatus(v); setOpen(null); }}
            className={`rounded-full border px-3 py-1 text-sm ${status === v ? 'bg-ink text-white border-ink' : 'bg-white border-border'}`}>{l} ({data?.counts?.[v] ?? 0})</button>
        ))}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin"/> : data.items.length === 0 ? <p className="text-sm text-ink/60" data-testid="partner-apps-empty">No applications here.</p> : (
        <div className="space-y-2">
          {data.items.map(a => (
            <div key={a.id} className="card-soft p-3">
              <button data-testid={`partner-app-${a.id}`} onClick={() => setOpen(open === a.id ? null : a.id)} className="w-full text-left flex flex-wrap items-center gap-3">
                <span className="pill">{PARTNER_LABELS[a.partner_type]}</span>
                <b>{a.company_name}</b><span className="text-sm text-ink/60">{a.city}, {a.state}</span>
                <span className="ml-auto text-xs text-ink/50">{new Date(a.created_at).toLocaleDateString()}</span>
              </button>
              {open === a.id && <div className="mt-3"><AppDetail id={a.id} onDone={() => { setOpen(null); load(); }}/></div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
